import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, ButtonGroup, Image, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSync, faEye, faDownload, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';

const FileManager = ({ beacons }) => {
  const [files, setFiles] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filter, setFilter] = useState({ type: '', beaconId: '' });

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      const response = await c2Service.getFiles(filter);
      // Backend returns {success, data: [...files]}
      const fileData = response.data?.data || response.data || [];
      setFiles(Array.isArray(fileData) ? fileData : []);
    } catch (error) {
      console.error('Failed to load files:', error);
      setFiles([]);
    }
  };

  const handleDownload = async (file) => {
    try {
      await c2Service.downloadFile(file._id, file.metadata?.filename || 'download');
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) {
      return;
    }

    try {
      await c2Service.deleteFile(fileId);
      loadFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const handlePreview = async (file) => {
    try {
      const response = await c2Service.getFilePreview(file._id);
      console.log('[FileManager] Preview response:', response.data);
      setSelectedFile({ ...file, fullData: response.data?.data || response.data });
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Failed to load file details:', error);
    }
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      screenshot: { bg: 'primary', label: 'Screenshot' },
      file_download: { bg: 'success', label: 'Download' },
      keylog: { bg: 'warning', label: 'Keylog' },
      process_list: { bg: 'info', label: 'Process List' },
      network_scan: { bg: 'secondary', label: 'Network Scan' },
      lsass_dump: { bg: 'danger', label: 'LSASS Dump' }
    };
    const config = typeConfig[type] || { bg: 'secondary', label: type };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const isImageFile = (file) => {
    return file.type === 'screenshot' || file.metadata?.mimeType?.startsWith('image/');
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Header style={{ padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
          <strong style={{ color: '#fff' }}>Filters</strong>
        </Card.Header>
        <Card.Body>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <Form.Label style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.5rem', display: 'block' }}>Type</Form.Label>
              <Form.Select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                style={{ width: '100%', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563' }}
              >
                <option value="">All types</option>
                <option value="screenshot">Screenshot</option>
                <option value="file_download">File Download</option>
                <option value="keylog">Keylog</option>
                <option value="process_list">Process List</option>
                <option value="network_scan">Network Scan</option>
                <option value="lsass_dump">LSASS Dump</option>
              </Form.Select>
            </div>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <Form.Label style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.5rem', display: 'block' }}>Beacon</Form.Label>
              <Form.Select
                value={filter.beaconId}
                onChange={(e) => setFilter({ ...filter, beaconId: e.target.value })}
                style={{ width: '100%', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563' }}
              >
                <option value="">All beacons</option>
                {beacons.map(beacon => (
                  <option key={beacon._id} value={beacon.beaconId}>
                    {beacon.beaconId} - {beacon.systemInfo?.hostname}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
            <Button size="sm" onClick={loadFiles}>
              <FontAwesomeIcon icon={faSearch} /> Apply Filters
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setFilter({ type: '', beaconId: '' });
                loadFiles();
              }}
            >
              Clear
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card style={{ backgroundColor: '#111827', border: '1px solid #374151' }}>
        <Card.Header style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
          <strong style={{ color: '#fff' }}>Files ({files?.length || 0})</strong>
          <button
            style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
            onClick={loadFiles}
          >
            <FontAwesomeIcon icon={faSync} /> Refresh
          </button>
        </Card.Header>
        <Card.Body className="p-0">
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Type</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Filename</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Beacon</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Size</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Path</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Captured</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(files) ? files : []).map(file => (
                <tr key={file._id} style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {getTypeBadge(file.type)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <strong>{file.metadata?.filename || 'Untitled'}</strong>
                    {isImageFile(file) && (
                      <Badge bg="light" text="dark" className="ms-2">Image</Badge>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <code className="small">{file.beacon?.beaconId || 'N/A'}</code>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    <small>
                      {formatFileSize(file.metadata?.size)}
                    </small>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    <small title={file.metadata?.path}>
                      {file.metadata?.path ? (
                        file.metadata.path.length > 30
                          ? '...' + file.metadata.path.slice(-30)
                          : file.metadata.path
                      ) : 'N/A'}
                    </small>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    <small>
                      {new Date(file.createdAt).toLocaleString()}
                    </small>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0.25rem' }}>
                      {isImageFile(file) && (
                        <button
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '0.25rem', cursor: 'pointer' }}
                          onClick={() => handlePreview(file)}
                        >
                          <FontAwesomeIcon icon={faEye} /> Preview
                        </button>
                      )}
                      <button
                        style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                        onClick={() => handleDownload(file)}
                      >
                        <FontAwesomeIcon icon={faDownload} /> Download
                      </button>
                      <button
                        style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                        onClick={() => handleDelete(file._id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {files.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem 0' }}>
              <p style={{ marginBottom: '0.5rem' }}>No files found</p>
              <small>Files will appear here after capture</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl">
        <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}>
          <Modal.Title style={{ color: '#f9fafb' }}>File Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#111827', color: '#f9fafb' }}>
          {selectedFile && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Filename:</strong> {selectedFile.metadata?.filename}
                </Col>
                <Col md={6}>
                  <strong>Type:</strong> {getTypeBadge(selectedFile.type)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Beacon:</strong> {selectedFile.beacon?.beaconId}
                </Col>
                <Col md={6}>
                  <strong>Size:</strong> {formatFileSize(selectedFile.metadata?.size)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Path:</strong> <code>{selectedFile.metadata?.path || 'N/A'}</code>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>SHA256:</strong> <code className="small">{selectedFile.metadata?.sha256 || 'N/A'}</code>
                </Col>
              </Row>

              <hr />

              {isImageFile(selectedFile) && selectedFile.fullData?.content && (
                <div className="text-center">
                  <Image
                    src={`data:${selectedFile.metadata?.mimeType || 'image/png'};base64,${selectedFile.fullData.content}`}
                    fluid
                    style={{ maxHeight: '600px' }}
                    alt="Screenshot preview"
                  />
                </div>
              )}

              {selectedFile.type === 'process_list' && selectedFile.fullData?.content && (
                <pre className="bg-dark text-light p-3" style={{ maxHeight: '500px', overflow: 'auto' }}>
                  {selectedFile.fullData.content}
                </pre>
              )}

              {selectedFile.type === 'keylog' && selectedFile.fullData?.content && (
                <>
                  <Alert variant="warning">
                    <strong><FontAwesomeIcon icon={faExclamationTriangle} /> Keylogging Data</strong>
                    <p className="mb-0 mt-1">Handle with care. This data may contain sensitive information.</p>
                  </Alert>
                  <pre className="bg-dark text-light p-3" style={{ maxHeight: '500px', overflow: 'auto' }}>
                    {selectedFile.fullData.content}
                  </pre>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1f2937', borderTop: '1px solid #374151' }}>
          <Button variant="success" onClick={() => handleDownload(selectedFile)}>
            Download
          </Button>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FileManager;
